/**
 * NavMenu — the header navigation, at both breakpoints.
 *
 * The markup is one render of the nav blocks that CSS reshapes at the xl breakpoint: above it, an
 * inline row in the header pill with absolutely positioned dropdown panels; below it, a panel
 * under the pill whose Level 1 items are accordions.
 *
 * A Level 1 label is always a link — hover opens its panel, click navigates. That rules out
 * `<details>`/`<summary>`, so panels are plain elements toggled here via the `hidden` attribute:
 *
 *   Desktop  open on hover after an intent delay, and on focus so the panel stays reachable by
 *            keyboard now that Enter navigates. Close after a short grace period on pointer leave,
 *            so a cursor travelling to the panel doesn't dismiss it. One panel at a time.
 *   Drawer   the chevron button beside each label toggles its panel. One panel at a time here too,
 *            so opening one collapses the other; hover plays no part. Every time the drawer opens
 *            the accordions reset to the editor's "Open by default" choice, not the last state.
 *
 * While anything is open the section's `[data-nav-overlay]` scrim is unhidden and fades in, and the
 * page behind it is scroll-locked: the wheel belongs to the panel, which scrolls internally.
 *
 * Deliberately NOT a `Component` subclass: this needs no `ref` collection and no declarative `on:`
 * bindings, and `usp-banner.js` sets the precedent for a plain element in `src/js/components/`.
 *
 * DOM contract:
 *   [data-nav-item]              a Level 1 <li>
 *   [data-nav-trigger]           the label inside it (an <a>, or a <span> when no link is set)
 *   [data-nav-disclosure]        the chevron button that toggles the panel below xl
 *   [data-nav-panel]             the panel, toggled by `hidden`
 *   [data-nav-swapped]           written here when a panel opens over another; CSS skips the reveal
 *   [data-nav-market="<id>"]     a nav footer's market trigger; opens the market modal by id
 *   [data-nav-hover-delay]       open-intent delay in ms, on the host
 *   [tabindex="-1"]              on the host, so `trapFocus` can land focus there on open
 *   [aria-controls="<host id>"]  the drawer toggle button, which lives outside the host
 */

import { trapFocus, removeTrapFocus } from "@theme/focus";

const DESKTOP_QUERY = "(min-width: 1280px)";
const DEFAULT_HOVER_DELAY = 120;
/** Grace period before a pointer leave closes the panel, so brief excursions don't dismiss it. */
const CLOSE_DELAY = 180;

class NavMenu extends HTMLElement {
  /** @type {number | null} */
  #openTimer = null;
  /** @type {number | null} */
  #closeTimer = null;
  /** Whether the `scroll-lock` attribute on `<html>` is this component's to remove. */
  #scrollLocked = false;
  #desktop = window.matchMedia(DESKTOP_QUERY);

  connectedCallback() {
    this.#toggle?.addEventListener("click", this.#onToggleClick);

    this.addEventListener("click", this.#onClick);
    this.addEventListener("pointerover", this.#onPointerOver);
    this.addEventListener("pointerleave", this.#onPointerLeave);
    this.addEventListener("focusin", this.#onFocusIn);

    // Escape is bound to the document, not the host: opening the drawer leaves focus on the toggle
    // button, which is a sibling in the pill, so a keydown there would never reach this element.
    document.addEventListener("keydown", this.#onKeydown);
    document.addEventListener("click", this.#onDocumentClick);
    this.#desktop.addEventListener("change", this.#onBreakpointChange);

    this.#openDefaults();
  }

  disconnectedCallback() {
    this.#toggle?.removeEventListener("click", this.#onToggleClick);

    this.removeEventListener("click", this.#onClick);
    this.removeEventListener("pointerover", this.#onPointerOver);
    this.removeEventListener("pointerleave", this.#onPointerLeave);
    this.removeEventListener("focusin", this.#onFocusIn);

    document.removeEventListener("keydown", this.#onKeydown);
    document.removeEventListener("click", this.#onDocumentClick);
    this.#desktop.removeEventListener("change", this.#onBreakpointChange);

    this.#clearTimers();
    this.#releaseDrawer();
    this.#syncOpenState();
  }

  /** The drawer toggle is a sibling in the pill, not a child, so it's found by what it controls. */
  get #toggle() {
    return /** @type {HTMLButtonElement | null} */ (
      this.id ? document.querySelector(`[data-nav-toggle][aria-controls="${CSS.escape(this.id)}"]`) : null
    );
  }

  /** @returns {HTMLElement[]} */
  get #panels() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll("[data-nav-panel]")]);
  }

  get #openPanels() {
    return this.#panels.filter((panel) => !panel.hidden);
  }

  /**
   * A bare `open` attribute rather than `data-open`, matching `cart-drawer-component`. That is
   * what `theme-editor.js` watches to persist and restore drawer state across editor reloads, so
   * a merchant tweaking a nav block doesn't have to reopen the drawer after every re-render.
   */
  get #drawerOpen() {
    return this.hasAttribute("open");
  }

  /** Public for `theme-editor.js`, which restores the drawer after a section re-render. */
  open() {
    this.#openDrawer();
  }

  close() {
    this.#closeDrawer({ restoreFocus: false });
  }

  // --- Panels ---------------------------------------------------------------------------

  /** @param {HTMLElement | null} panel */
  #openPanel(panel) {
    if (!panel || !panel.hidden) return;

    // Only the desktop reveal transition cares about a swap; below xl `hidden` toggles instantly.
    const swapping = this.#desktop.matches && this.#openPanels.length > 0;

    // One at a time at both breakpoints: the drawer is an exclusive accordion.
    this.#closeAll();
    panel.toggleAttribute("data-nav-swapped", swapping);

    panel.hidden = false;
    this.#setExpanded(panel, true);
    panel.closest("[data-nav-item]")?.setAttribute("data-nav-active", "");
    this.#syncOpenState();
  }

  /** @param {HTMLElement | null} panel */
  #closePanel(panel) {
    if (!panel || panel.hidden) return;

    panel.hidden = true;
    this.#setExpanded(panel, false);
    panel.closest("[data-nav-item]")?.removeAttribute("data-nav-active");
    this.#syncOpenState();
  }

  #closeAll() {
    for (const panel of this.#openPanels) this.#closePanel(panel);
  }

  /**
   * Below xl, the first panel flagged `data-nav-default-open` starts expanded, so the drawer opens
   * with that section already unfolded. Only the first: the accordion is exclusive, so a second
   * flag would silently win over the first. Desktop is left alone: a dropdown open on page load
   * would sit over the content. Runs on connect, after a breakpoint change resets everything, and
   * on every drawer open so a panel the visitor collapsed comes back as the editor configured it.
   */
  #openDefaults() {
    if (this.#desktop.matches) return;

    this.#openPanel(/** @type {HTMLElement | null} */ (this.querySelector("[data-nav-panel][data-nav-default-open]")));
  }

  /**
   * Every disclosure pointing at this panel, so `aria-expanded` never drifts from `hidden`.
   * @param {HTMLElement} panel
   * @param {boolean} expanded
   */
  #setExpanded(panel, expanded) {
    if (!panel.id) return;

    for (const control of this.querySelectorAll(`[aria-controls="${CSS.escape(panel.id)}"]`)) {
      control.setAttribute("aria-expanded", String(expanded));
    }
  }

  /**
   * The scrim behind an open panel or drawer. It sits outside `header-component` — a transformed
   * ancestor would break its `position: fixed` — so it's found through the section, and toggled by
   * `hidden`, which is what its `[&:not([hidden])]:opacity-100` utilities key off.
   */
  get #overlay() {
    return /** @type {HTMLElement | null} */ (
      this.closest(".header-section")?.querySelector("[data-nav-overlay]") ?? null
    );
  }

  #syncOpenState() {
    // Below xl the panels are accordions inside the drawer, so only the drawer decides whether the
    // page is covered — otherwise a default-open panel would lock scrolling on load and keep it
    // locked after the drawer closes.
    const panelsOpen = this.#desktop.matches && this.#openPanels.length > 0;
    const open = this.isConnected && (this.#drawerOpen || panelsOpen);

    this.#setScrollLock(open);

    const overlay = this.#overlay;
    if (overlay) overlay.hidden = !open;
  }

  /**
   * While anything is open the page behind it must not scroll — the wheel belongs to the panel,
   * which carries its own full-height scroll region at both breakpoints. This is also what keeps a
   * panel open across a scroll: it is positioned against the pill, and `header.js` reads the same
   * attribute to hold the header stack in place rather than sliding it, and the panel, out of view.
   *
   * `dialog.js` writes the same attribute for a `<details scroll-lock>`, so ownership is tracked
   * here: a lock someone else took is neither re-taken nor released, which is what stops a
   * disconnect — a theme-editor re-render, say — from freeing the viewport under an open dialog.
   *
   * @param {boolean} locked
   */
  #setScrollLock(locked) {
    if (locked) {
      if (this.#scrollLocked || document.documentElement.hasAttribute("scroll-lock")) return;

      this.#scrollLocked = true;
      document.documentElement.setAttribute("scroll-lock", "");
    } else if (this.#scrollLocked) {
      this.#scrollLocked = false;
      document.documentElement.removeAttribute("scroll-lock");
    }
  }

  /** @param {Element | null | undefined} el */
  static #panelFor(el) {
    return /** @type {HTMLElement | null} */ (el?.closest("[data-nav-item]")?.querySelector("[data-nav-panel]"));
  }

  // --- Desktop hover / focus -------------------------------------------------------------

  /** @param {PointerEvent} event */
  #onPointerOver = (event) => {
    if (!this.#desktop.matches) return;

    // `pointerover` bubbles, unlike `pointerenter`, so one listener on the host covers every item
    // including the panel contents — which is what keeps an open panel from closing under the
    // cursor.
    const item = /** @type {HTMLElement | null} */ (event.target)?.closest?.("[data-nav-item]");
    if (!item) return;

    this.#clearTimers();

    const panel = /** @type {HTMLElement | null} */ (item.querySelector("[data-nav-panel]"));

    // A childless Level 1 still dismisses whatever is open — moving onto it should clear the
    // previous panel rather than leave it hanging.
    if (!panel) {
      this.#closeAll();
      return;
    }

    if (!panel.hidden) return;

    // Once a panel is open the nav is in "browsing" mode, so swapping is immediate. The delay only
    // guards the first open, which is the one a passing cursor would trigger by accident.
    if (this.#openPanels.length > 0) {
      this.#openPanel(panel);
      return;
    }

    const configured = Number(this.dataset.navHoverDelay);
    const delay = Number.isFinite(configured) && configured >= 0 ? configured : DEFAULT_HOVER_DELAY;
    this.#openTimer = window.setTimeout(() => this.#openPanel(panel), delay);
  };

  #onPointerLeave = () => {
    if (!this.#desktop.matches) return;

    this.#clearTimers();
    this.#closeTimer = window.setTimeout(() => this.#closeAll(), CLOSE_DELAY);
  };

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    if (!this.#desktop.matches) return;

    const item = /** @type {HTMLElement | null} */ (event.target)?.closest?.("[data-nav-item]");
    if (!item) return;

    this.#clearTimers();

    const panel = /** @type {HTMLElement | null} */ (item.querySelector("[data-nav-panel]"));
    if (panel) this.#openPanel(panel);
    else this.#closeAll();
  };

  #clearTimers() {
    if (this.#openTimer !== null) {
      clearTimeout(this.#openTimer);
      this.#openTimer = null;
    }

    if (this.#closeTimer !== null) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  }

  // --- Disclosure buttons ----------------------------------------------------------------

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);

    const market = target?.closest?.("[data-nav-market]");
    if (market && this.contains(market)) {
      event.preventDefault();
      this.#openMarketModal(market.getAttribute("data-nav-market"));
      return;
    }

    const disclosure = target?.closest?.("[data-nav-disclosure]");
    if (!disclosure || !this.contains(disclosure)) return;

    event.preventDefault();

    const panel = NavMenu.#panelFor(disclosure);
    if (!panel) return;

    if (panel.hidden) this.#openPanel(panel);
    else this.#closePanel(panel);
  };

  /**
   * Opens the market modal named by `[data-nav-market]`, closing the nav's own surfaces first
   * so the dialog isn't layered over an open panel or drawer.
   *
   * @param {string | null} id
   */
  #openMarketModal(id) {
    if (!id) return;

    const modal = /** @type {(HTMLElement & { showDialog?: () => void }) | null} */ (document.getElementById(id));

    if (typeof modal?.showDialog !== "function") return;

    this.#clearTimers();
    this.#closeAll();
    this.#closeDrawer({ restoreFocus: false });
    modal.showDialog();
  }

  // --- Drawer ---------------------------------------------------------------------------

  #onToggleClick = () => {
    if (this.#drawerOpen) this.#closeDrawer();
    else this.#openDrawer();
  };

  #openDrawer() {
    this.setAttribute("open", "");
    this.#toggle?.setAttribute("aria-expanded", "true");

    this.#closeAll();
    this.#openDefaults();

    trapFocus(this);
    this.#syncOpenState();
  }

  #closeDrawer({ restoreFocus = true } = {}) {
    if (!this.#drawerOpen) return;

    this.removeAttribute("open");
    this.#toggle?.setAttribute("aria-expanded", "false");
    this.#releaseDrawer();
    this.#syncOpenState();

    if (restoreFocus) this.#toggle?.focus();
  }

  /** The lock is released by `#syncOpenState`, which every caller of this runs straight after. */
  #releaseDrawer() {
    removeTrapFocus();
  }

  // --- Shared ---------------------------------------------------------------------------

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== "Escape") return;

    // The drawer is checked first because below xl its panels are accordions: Escape dismisses the
    // whole modal, not the section the cursor happens to be in. `#closeDrawer` returns focus to the
    // toggle, so the cross the user pressed Escape over stays the active element.
    if (this.#drawerOpen) {
      this.#closeDrawer();
      return;
    }

    const [panel] = this.#openPanels;
    if (!panel) return;

    // Focus only moves if it was inside the panel being dismissed — otherwise it would land on
    // <body> — and never when Escape came from elsewhere on the page, since this listener is on the
    // document and a hover-opened panel can be dismissed while focus sits in, say, the search field.
    const restoreFocus = panel.contains(document.activeElement);
    const trigger = panel.closest("[data-nav-item]")?.querySelector("[data-nav-trigger]");

    this.#closeAll();
    if (restoreFocus) /** @type {HTMLElement | null} */ (trigger)?.focus?.();
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    const target = /** @type {Node} */ (event.target);
    if (this.contains(target) || this.#toggle?.contains(target)) return;

    this.#clearTimers();
    this.#closeAll();
    this.#closeDrawer({ restoreFocus: false });
  };

  /** Crossing the breakpoint leaves the other mode's state behind, so both are reset. */
  #onBreakpointChange = () => {
    this.#clearTimers();
    this.#closeAll();
    this.#closeDrawer({ restoreFocus: false });
    this.#openDefaults();
  };
}

if (!customElements.get("nav-menu")) {
  customElements.define("nav-menu", NavMenu);
}
